import { useMemo } from 'react';
import { Search, X } from 'lucide-react';
import { sideOf, type Competition, type ForecastEntry, type Match, type Tournament } from '@airmail/engine';
import { Input, SectionHead, cx } from '@/ui';
import { MatchRow } from '@/components/MatchCard';
import { courtLabel } from '@/lib/teams';
import { fmtTime } from '@/lib/format';
import { useT } from '@/i18n';

/**
 * „Beendet" auf der Spiele-Seite — und auf Wunsch **alle Partien eines Teams**.
 *
 * Harald (10/2026): „Lässt sich einbauen, dass man die Paarungen einer
 * Mannschaft und deren Spielergebnisse abrufen kann? Das würde ziemlich viel
 * Sucherei ersparen, wenn es Unklarheiten über das Ergebnis abgeschlossener
 * Partien gibt." Ohne Suche zeigt der Abschnitt wie bisher die letzten
 * Ergebnisse. Mit Suche (Team, Spieler, Verein) stehen je passendem Team alle
 * seine Partien da: gespielt mit Ergebnis und Sieg/Niederlage, laufend mit
 * Feld, offen mit geschätzter Zeit. Ein Klick auf ein gespieltes Spiel öffnet
 * wie gewohnt den Ergebnis-Dialog zum Korrigieren.
 *
 * Gesteuert von außen (`suche`, `teamId`), damit ein Klick auf ein Team bei
 * „Wann spielt wer?" genau dieses Team hier aufschlägt — ohne dass „Team 1"
 * auch „Team 10" bis „Team 19" mitbringt.
 */
const MAX_TEAMS = 8;

export function TeamPartien({
  comp,
  tournament,
  zuletzt,
  forecast,
  suche,
  teamId,
  onSuche,
  onMatch,
}: {
  comp: Competition;
  tournament: Tournament;
  /** Die letzten Ergebnisse — was ohne Suche erscheint */
  zuletzt: Match[];
  forecast?: Map<string, ForecastEntry>;
  suche: string;
  /** Genau dieses Team (Klick bei „Wann spielt wer?"); sonst zählt die Suche */
  teamId?: string;
  onSuche: (suche: string, teamId?: string) => void;
  onMatch?: (m: Match) => void;
}) {
  const t = useT();
  const needle = suche.trim().toLowerCase();

  const teams = useMemo(() => {
    if (teamId) return comp.teams.filter((x) => x.id === teamId);
    if (!needle) return [];
    return comp.teams.filter((x) => [x.name, x.club ?? '', ...x.players.flatMap((p) => [p.firstName, p.lastName, `${p.firstName} ${p.lastName}`])].some((s) => s.toLowerCase().includes(needle)));
  }, [comp.teams, needle, teamId]);

  // Alle Spiele in der Reihenfolge des Turniers: Phase, Runde, Nummer
  const geordnet = useMemo(() => {
    const out: Match[] = [];
    for (const p of comp.phases) out.push(...[...p.matches].sort((a, b) => a.round - b.round || a.number - b.number));
    return out;
  }, [comp.phases]);

  const imSpiel = (m: Match, id: string) => sideOf(m, id) !== null;
  const gespielt = (m: Match) => m.status === 'finished' || m.status === 'walkover';
  const aktiv = !!teamId || !!needle;
  const gezeigt = teams.slice(0, MAX_TEAMS);
  const anzahl = gezeigt.reduce((n, team) => n + geordnet.filter((m) => m.status !== 'cancelled' && imSpiel(m, team.id)).length, 0);

  /** Rechts in der Zeile: Ergebnis aus Sicht des Teams, sonst Zeit oder Feld */
  const rechts = (m: Match, id: string) => {
    if (gespielt(m)) {
      const seite = sideOf(m, id);
      const w = m.result?.winner;
      return (
        <div className="text-right shrink-0">
          {m.annulled ? (
            <span className="label text-dim">{t('annulliert')}</span>
          ) : w ? (
            <span className={cx('label', w === seite ? 'text-live' : 'text-red')}>{w === seite ? t('Sieg') : t('Niederlage')}</span>
          ) : (
            <span className="label text-dim">{t('Unentschieden')}</span>
          )}
          {m.endedAt && <div className="font-mono text-[11px] text-dim">{fmtTime(m.endedAt)}</div>}
        </div>
      );
    }
    if (m.status === 'running') {
      return (
        <div className="text-right shrink-0">
          <span className="font-mono text-[13px] text-live">{t('spielt gerade')}</span>
          {m.courtId && <div className="text-[11px] text-dim">{courtLabel(tournament, m.courtId)}</div>}
        </div>
      );
    }
    const f = forecast?.get(m.id);
    return (
      <div className="text-right shrink-0">
        <span className="font-mono text-[13px] text-muted">{f ? `~${fmtTime(f.startsAt)}` : t('offen')}</span>
        {f && <div className="text-[11px] text-dim">{courtLabel(tournament, f.courtId)}</div>}
      </div>
    );
  };

  return (
    <section className="mt-8 scroll-mt-16" id="partien" data-team-partien>
      <SectionHead title={aktiv ? t('Partien') : t('Beendet')} meta={aktiv ? t('{n} Partien', { n: anzahl }) : t('zuletzt {n}', { n: zuletzt.length })}>
        <label className="flex items-center gap-1.5">
          <Search size={13} className="text-dim" />
          <Input
            className="w-56 max-w-full py-1 text-sm"
            placeholder={t('Partien eines Teams …')}
            value={suche}
            onChange={(e) => onSuche(e.target.value)}
            aria-label={t('Partien eines Teams suchen')}
            data-team-partien-suche
          />
        </label>
        {aktiv && (
          <button type="button" className="text-dim hover:text-ink cursor-pointer p-1" onClick={() => onSuche('')} aria-label={t('Suche leeren')} title={t('Suche leeren')}>
            <X size={14} />
          </button>
        )}
      </SectionHead>

      {!aktiv ? (
        zuletzt.length === 0 ? (
          <div className="text-sm text-dim">{t('Noch kein Ergebnis.')}</div>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(min(300px,100%),1fr))] gap-1.5">
            {zuletzt.map((m) => (
              <MatchRow key={m.id} match={m} onClick={onMatch ? () => onMatch(m) : undefined} right={<span className="font-mono text-[11px] text-dim">{fmtTime(m.endedAt)}</span>} />
            ))}
          </div>
        )
      ) : teams.length === 0 ? (
        <div className="text-sm text-dim">{t('Kein Team passt zu „{q}“.', { q: suche })}</div>
      ) : (
        <div className="grid gap-5">
          {gezeigt.map((team) => {
            const spiele = geordnet.filter((m) => m.status !== 'cancelled' && imSpiel(m, team.id));
            const fertig = spiele.filter((m) => gespielt(m) && !m.annulled);
            const siege = fertig.filter((m) => m.result?.winner === sideOf(m, team.id)).length;
            return (
              <div key={team.id} data-team-partien-team={team.id}>
                <div className="flex items-baseline gap-3 mb-1.5 flex-wrap">
                  <b className="tracking-tight">{team.name}</b>
                  {team.club && <span className="text-xs text-dim">{team.club}</span>}
                  <span className="font-mono text-[11px] text-dim ml-auto">
                    {t('Bilanz {s}:{n} · {o} offen', { s: siege, n: fertig.length - siege, o: spiele.length - spiele.filter(gespielt).length })}
                  </span>
                </div>
                {spiele.length === 0 ? (
                  <div className="text-sm text-dim">{t('Noch keine Partien ausgelost.')}</div>
                ) : (
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(min(300px,100%),1fr))] gap-1.5">
                    {spiele.map((m) => (
                      <MatchRow key={m.id} match={m} muted={m.annulled} onClick={onMatch && (gespielt(m) || m.status === 'running') ? () => onMatch(m) : undefined} right={rechts(m, team.id)} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {teams.length > MAX_TEAMS && <div className="text-xs text-dim">{t('{n} weitere Teams passen — Suche genauer fassen.', { n: teams.length - MAX_TEAMS })}</div>}
        </div>
      )}
    </section>
  );
}

/** Für „Wann spielt wer?": das Team anklicken → hier aufschlagen */
export function zuTeamPartien(): void {
  document.getElementById('partien')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}
